import {Avatar, Modal} from 'antd'
import {
    comment_addComment_Api,
    comment_addReply_Api,
    comment_getComments_Api,
    comment_getReples_Api,
    comment_removeComment_Api,
    commentContainerSchema,
} from 'apis'
import {getId, getType} from 'apis/model/base'
import {socketUnionSchema} from 'apis/schema'
import {commentSchema} from 'apis/schema/commentSchema'
import classNames from 'classnames'
import EmptyHolder from 'components/EmptyHolder'
import Description from 'components/Feed/Description'
import {MediaPlayer} from 'components/Feed/VideoEmbed'
import {ExtraLike} from 'components/Feed/Like'
import {get_mention_data, getText} from 'components/Feed/MentionDataContext'
import {isExtraVoting, VoteTypes} from 'components/Feed/Vote'
import InputBar from 'components/InputBar'
import {LoginContext} from 'components/LoginContext'
import {NullAsDefault, renderOwnChild} from "views/Shared";
import {SelectEntityItem} from 'components/SelectEntityItem'
import getLinkToDetail from 'helpers/getLinkToDetail'
import getTitle from 'helpers/getTitle'
import logFunc from 'helpers/logFunc'
import _ from 'lodash'
import AsyncAction from 'modules/asyncCache/components/AsyncAction'
import {LazyPagination} from 'modules/asyncCache/components/LazyPagination'
import useTranslate from 'modules/local/useTranslate'
import {SocketContext} from 'modules/socket/SocketContext'
import {JOIN_ROOM} from 'modules/socket/types'
import {normalize} from 'normalizr'
import React, {useCallback, useContext, useEffect, useMemo, useState,} from 'react'
import {BsPersonFill} from 'react-icons/bs'
import {useDispatch} from 'react-redux'
import {Link} from 'react-router-dom'
import useToggle from 'react-use/lib/useToggle'
import {nest} from 'recompose'
import {useSelectEntities} from 'redux/entities/useSelectEntities'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {renderLoading} from 'views/MainPage/functions/renderPagingLoading'
import {GoogleAdsRecommendedUnitHorizontal} from '../../components/GoogleAds/GoogleAds'
import {LayoutContext} from '../../components/layouts/Default/LayoutContext'
import {useExtraVote, useVote} from '../Server/ServerContainer'
import {renderIf} from '../Shared'
import ResponsesEditAction from './components/ResponsesEditAction'
import FeedContext from "../../components/Feed/FeedContext";
import {FiEdit3, FiTrash2} from "react-icons/fi";
import {formatRelativeTime} from "../../helpers/dateUtils";
import {BiSolidChat, BiSolidLike} from "react-icons/bi";
import {formatNumber} from "../../helpers/number";
import {IoIosChatbubbles} from "react-icons/io";
import getAvatar from "../../helpers/getAvatar";
import {GoDotFill} from "react-icons/go";
import {FaReply} from "react-icons/fa6";
import {PiTrash} from "react-icons/pi";
import {createAsyncAction} from "../../modules/asyncCache";
import {useCommentHandler} from "../../components/Feed/Ask/AskContentWrapper";
import ReactPlayer from "react-player";

const MAX_INDENT_DEPTH = 3

const VIDEO_URL_REGEX = /https?:\/\/[^\s]+/g

// Comment/reply không lưu embed_data (khác bài post) — nhận diện link video
// playable ngay trong nội dung đã lưu để render ReactPlayer lúc hiển thị
const extractPlayableVideoUrl = (text) => {
    const urls = String(text ?? '').match(VIDEO_URL_REGEX) ?? []
    return urls.find((url) => ReactPlayer.canPlay(url)) ?? null
}

const ActionIconButton = ({icon, onClick, colorClass, active = false, activeClass}) => (
    <span
        onClick={onClick}
        style={{width: 24, height: 24}}
        className={classNames(
            'flex flex-center rounded-full cursor-pointer transition-colors',
            active ? activeClass : classNames(colorClass, 'hover:background-200')
        )}>
        {icon}
    </span>
)

const Comment = ({item, className, canReply = true, depth = 0}) => {

    const t = useTranslate()
    const login = useContext(LoginContext)
    const {isLg} = useContext(LayoutContext)

    const [openEdit, toggleOpenEdit] = useToggle()
    const [openReplies, toggleOpenReplies] = useToggle()
    const isReply = !!item?.parent_id;
    const isDeepReply = depth > MAX_INDENT_DEPTH;
    const isDeleted = !!(item?.isDeleted || item?.is_deleted);

    const dispatch = useDispatch()
    const {creator, is_author, vote_status, total_votes, total_comments, total_replies, id} = item ?? {}
    const handleVote = useVote({
        vote_status,
        total_votes,
        owner_id: id,
        owner_type: getType(item),
    })

    const {avatar, thumbnail} = creator ?? {}
    const isCreator = getId(creator) === getId(login)
    const canManage = !!item?.edit
    const videoUrl = useMemo(() => extractPlayableVideoUrl(item?.message), [item?.message])

    const confirmDeleteComment = () => {
        const modal = Modal.confirm({
            icon: <PiTrash className="anticon"/>,
            title: t('are you sure delete this comment'),
            className: 'custom-modal',
            okText: t('sure'),
            cancelText: t('ignore'),
            okButtonProps: {
                className: 'no-shadow no-text-shadow no-border rounded-lg',
                color: 'primary', variant: 'solid'
            },
            cancelButtonProps: {
                className: 'no-shadow no-text-shadow rounded-lg no-border',
            },
            onOk: () => {
                dispatch(
                    createAsyncAction({
                        apiInfo: comment_removeComment_Api,
                        query: {
                            ':prop': item.container_type,
                            ':id': item.container_id,
                            ':comment_id': item.id,
                        },
                        onSuccess: () => {
                            successNotify(NotificationActionTypes.DELETE, t)
                        },
                    })
                )
            },
            onCancel: () => modal.destroy(),
        })
    }

    return (
        <div className={classNames(
            'custom-item-render relative',
            isReply ? 'py-2' : 'mb-3',
            className,
        )}>
            <div className={classNames('flex items-start relative', !isReply && 'gap-3')}>
                {!isReply && (
                    <Avatar
                        size={36}
                        className="mt-0.5 flex flex-center background-200 flex-shrink-0"
                        icon={<BsPersonFill size={18} className="text-color-400"/>}
                        src={avatar || thumbnail}
                    />
                )}
                <div className="flex flex-col flex-1 min-w-0">
                    <div className={classNames(
                        "bg-slate-50 rounded-xl flex flex-col gap-1 relative",
                        isDeepReply ? "px-2 pt-1.5 pb-1.5" : "px-3 pt-2 pb-3",
                    )}>
                        {/* Header */}
                        <div className={classNames('flex', isReply ? 'items-start gap-2' : 'flex-col gap-0.5')}>
                            {isReply && (
                                <Avatar
                                    size={isDeepReply ? 16 : 20}
                                    className="mt-0.5 flex flex-center background-200 flex-shrink-0"
                                    icon={<BsPersonFill size={isDeepReply ? 9 : 11} className="text-color-400"/>}
                                    src={avatar || thumbnail}
                                />
                            )}
                            <div className={classNames('flex flex-col gap-0.5', isReply && 'flex-1 min-w-0')}>
                                <Link
                                    rel="nofollow"
                                    to={getLinkToDetail(creator)}
                                    className="flex items-center text-gray-900 no-underline min-w-0">
                                    <span className={classNames('font-bold tracking-wide break-words', isReply ? 'text-xs' : 'text-sm')}>
                                        {getTitle(creator)}
                                    </span>
                                </Link>
                                <div className="flex items-center flex-wrap gap-1.5">
                                    {isCreator && (
                                        <span
                                            style={{ padding: '2px 8px', fontSize: 10 }}
                                            className="flex flex-center bg-secondary-100 rounded-md font-bold text-secondary-600 tracking-wider">
                                            {t('yourself')}
                                        </span>
                                    )}
                                    <span className="text-2xs text-slate-400 tracking-wider">
                                        {formatRelativeTime(item?.created)}
                                    </span>
                                </div>
                            </div>
                        </div>
                        {/* Content */}
                        <div className={classNames("pb-1", isReply ? "mt-0.5" : "mt-1")}>
                            {isDeleted ? (
                                <div className={classNames('italic text-color-400', isReply ? 'text-xs' : 'text-sm')}>
                                    {t('this comment has been deleted')}
                                </div>
                            ) : openEdit ? (
                                <ResponsesEditAction onExit={toggleOpenEdit} item={item}/>
                            ) : (
                                <div className="flex flex-col gap-1">
                                    <Description
                                        className={classNames('text-slate-700 tracking-wide', isReply ? 'text-xs' : 'text-sm')}
                                        type="post" data={item}
                                        mentions={_.get(item, 'mentions')}>
                                        {_.get(item, 'message')}
                                    </Description>
                                    {!!_.get(item, 'photos', [])?.length && (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {_.get(item, 'photos', []).map((photo, photoIndex) => (
                                                <img key={photoIndex} src={photo.path}
                                                     className="rounded object-cover"
                                                     style={{width: 80, height: 80}}/>
                                            ))}
                                        </div>
                                    )}
                                    {!!videoUrl && (
                                        <div className="mt-1" style={{maxWidth: isReply ? 260 : 360}}>
                                            <MediaPlayer url={videoUrl}/>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {/* Stats */}
                        {_.some([total_votes > 0, total_replies > 0]) && (
                            <div
                                style={{ padding: '0 6px', bottom: -12, right: 10 }}
                                className="flex flex-center background border border-color-100 rounded-full absolute">
                                <div className="flex items-center gap-1">
                                    <Avatar.Group max={{count: 2}} className="items-center">
                                        <Avatar
                                            size={14} icon={<BiSolidLike size={8} className="text-white"/>}
                                            style={{ border: '1px solid #fff' }}
                                            className="flex flex-center bg-blue-600"
                                        />
                                        <Avatar
                                            size={14} icon={<BiSolidChat size={8} className="text-white"/>}
                                            style={{ border: '1px solid #fff' }}
                                            className="flex flex-center bg-green-500"
                                        />
                                    </Avatar.Group>
                                    <span className="font-medium text-xs text-color-100 tracking-wider">
                                        {formatNumber(Number(total_votes + total_replies))}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    {/* Actions */}
                    <div className={classNames('mt-2 mx-2 flex items-center flex-wrap gap-x-3 gap-y-1', isReply && 'text-xs')}>
                        {!isDeleted && (
                            isReply ? (
                                <ActionIconButton
                                    onClick={() => handleVote()}
                                    active={!!item?.vote_status}
                                    activeClass="text-secondary-600 background-100"
                                    colorClass="text-gray-500"
                                    icon={<BiSolidLike size={13}/>}
                                />
                            ) : (
                                <span
                                    onClick={() => handleVote()}
                                    className={classNames(
                                        'font-medium tracking-wide cursor-pointer', !!item?.vote_status ? 'text-secondary-600' : 'text-gray-700 hover:text-secondary'
                                    )}>
                                    {t(!!vote_status ? 'liked' : 'like')}
                                </span>
                            )
                        )}
                        {canReply && (
                            <div
                                onClick={toggleOpenReplies}
                                className="flex items-center gap-1 text-gray-700 hover:text-secondary cursor-pointer">
                                <GoDotFill size={8} />
                                <span className="font-medium tracking-wide">{t('reply')}</span>
                            </div>
                        )}
                        {!isDeleted && canManage && (
                            <div className={classNames('flex items-center contextIcon', isReply ? 'gap-1' : 'gap-x-4')}>
                                {isReply ? (
                                    <ActionIconButton
                                        onClick={toggleOpenEdit}
                                        colorClass="text-blue-500"
                                        icon={<FiEdit3 size={13}/>}
                                    />
                                ) : (
                                    <span
                                        onClick={toggleOpenEdit}
                                        className="flex items-center text-blue-500 hover:text-blue-700 cursor-pointer">
                                        <FiEdit3 size={12} className="mr-1"/>
                                        <span className="font-normal text-xs tracking-wide">{t('edit')}</span>
                                    </span>
                                )}
                                {isReply ? (
                                    <ActionIconButton
                                        onClick={confirmDeleteComment}
                                        colorClass="text-primary-600"
                                        icon={<FiTrash2 size={13}/>}
                                    />
                                ) : (
                                    <span
                                        onClick={confirmDeleteComment}
                                        className="flex items-center text-primary-600 hover:text-primary cursor-pointer">
                                        <FiTrash2 size={12} className="mr-1"/>
                                        <span className="font-normal text-xs tracking-wide">{t('delete')}</span>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    {total_replies > 0 && (
                        <span
                            onClick={toggleOpenReplies}
                            className="mt-1 mx-2 font-medium text-slate-500 hover:text-secondary-600 cursor-pointer">
                            <FaReply size={12} className="rotate-180 mr-1 text-slate-400"/>
                            {t('view {n} community responses', {n: total_replies})}
                        </span>
                    )}
                    {/* Replies */}
                    {openReplies && (
                        <div className="mt-2">
                            <Replies
                                comment_id={id}
                                id={item?.container_id}
                                type={item?.container_type}
                                depth={depth + 1}
                            />
                        </div>
                    )}
                </div>
            </div>

        </div>
    )
}

export const VoteComment = ({item, ref_id, className, canReply = true, depth = 0}) => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    const {isSm} = useContext(LayoutContext)
    const {item: feed} = useContext(FeedContext)

    const dispatch = useDispatch()
    const [openEdit, toggleOpenEdit] = useToggle()

    const [openReplies, toggleOpenReplies] = useToggle()

    const {
        creator,
        created,
        message,
        container_id,
        container_type,
        vote_status,
        total_votes,
        total_downvotes,
        total_replies,
    } = item ?? {}

    const {name = '', avatar, thumbnail} = creator ?? {}

    const isDeleted = !!(_.get(item, 'isDeleted', false) || _.get(item, 'is_deleted', false))

    const mentions = Array.from(item?.mentions ?? [])

    const isReply = !!item?.parent_id;

    const messageContent = useMemo(() => {
        if (isDeleted) {
            return (
                <div className={classNames('italic text-color-400', isReply ? 'text-xs' : 'text-sm')}>
                    {t('this comment has been deleted')}
                </div>
            )
        }
        return (
            <Description
                data={item} mentions={mentions} type={container_type}
                className={classNames('text-slate-700 tracking-wide', isReply ? 'text-xs' : 'text-sm')}>
                {message}
            </Description>
        )
    }, [isDeleted, message, mentions, isReply, t])

    const videoUrl = useMemo(() => extractPlayableVideoUrl(message), [message])

    const isDeepReply = depth > MAX_INDENT_DEPTH;
    const showReplies = !!canReply

    const Wrapper = !!canReply ? renderOwnChild : renderOwnChild

    const hasExtraVote = !isDeleted

    const handleExtraVote = useExtraVote({
        vote_status,
        owner_id: getId(item),
        owner_type: getType(item),
    })

    const vote = useCallback(
        (value) => {
            if (isExtraVoting(vote_status) && value === vote_status) {
                handleExtraVote(VoteTypes.NONE)
            } else {
                handleExtraVote(value)
            }
        },
        [handleExtraVote, vote_status]
    )

    const [bestCommentAction, {invisible = false, isBestComment = false}] =
        useCommentHandler({
            type: getType(feed),
            id: getId(feed),
            comment_id: getId(item),
        })

    const isAuthor = getId(creator) === getId(feed?.creator)
    const isCreator = getId(creator) === getId(login)
    const canManage = !!item?.edit

    const confirmDeleteComment = () => {
        const modal = Modal.confirm({
            icon: <PiTrash className="anticon"/>,
            title: t('are you sure delete this comment'),
            className: 'custom-modal',
            okText: t('sure'),
            cancelText: t('ignore'),
            okButtonProps: {
                className: 'no-shadow no-text-shadow no-border rounded-lg',
                color: 'primary', variant: 'solid'
            },
            cancelButtonProps: {
                className: 'no-shadow no-text-shadow rounded-lg no-border',
            },
            onOk: () => {
                dispatch(
                    createAsyncAction({
                        apiInfo: comment_removeComment_Api,
                        query: {
                            ':prop': item.container_type,
                            ':id': item.container_id,
                            ':comment_id': item.id,
                        },
                        onSuccess: () => {
                            successNotify(NotificationActionTypes.DELETE, t)
                        },
                    })
                )
            },
            onCancel: () => modal.destroy(),
        })
    }

    const get_decrypted_values = useCallback((values) => {
        const {message, mentions} = values ?? {}
        const description_data = get_mention_data(mentions) ?? undefined
        return {
            ...(values ?? {}),
            description_data,
            message: getText(message, get_mention_data(mentions, true)),
        }
    }, [])

    return (
        <Wrapper item={item} ref_id={ref_id} className="mb-4 pb-2" >
            <div className={classNames('pt-2 custom-item-render  relative', className)}>
                <div className={classNames('flex items-start relative', !isReply && 'gap-3')}>
                    {!isReply && (
                        <Avatar
                            size={36}
                            className="mt-0.5 flex flex-center background-200 flex-shrink-0"
                            icon={<BsPersonFill size={18} className="text-color-400"/>}
                            src={avatar || thumbnail}
                        />
                    )}
                    <div className="flex flex-col flex-1 min-w-0">
                        <div className={classNames(
                            "bg-slate-50 rounded-xl flex flex-col gap-1 relative",
                            isDeepReply ? "px-2 pt-1.5 pb-1.5" : "px-3 pt-2 pb-3",
                        )}>
                            <div className={classNames('flex', isReply ? 'items-start gap-2' : 'flex-col gap-0.5')}>
                                {isReply && (
                                    <Avatar
                                        size={isDeepReply ? 16 : 20}
                                        className="mt-0.5 flex flex-center background-200 flex-shrink-0"
                                        icon={<BsPersonFill size={isDeepReply ? 9 : 11} className="text-color-400"/>}
                                        src={avatar || thumbnail}
                                    />
                                )}
                                <div className={classNames('flex flex-col gap-0.5', isReply && 'flex-1 min-w-0')}>
                                    <Link
                                        rel="nofollow"
                                        to={getLinkToDetail(creator)}
                                        className="flex items-center text-gray-900 no-underline min-w-0">
                                        <span className={classNames('font-bold tracking-wide break-words', isReply ? 'text-xs' : 'text-sm')}>
                                            {getTitle(creator)}
                                        </span>
                                    </Link>
                                    <div className="flex items-center flex-wrap gap-1.5">
                                        {isCreator && (
                                            <span
                                                style={{ padding: '2px 8px', fontSize: 10 }}
                                                className="flex flex-center bg-secondary-100 rounded-md font-bold text-secondary-600 tracking-wider">
                                                {t('yourself')}
                                            </span>
                                        )}
                                        <span className="text-2xs text-slate-400 tracking-wider">
                                            {formatRelativeTime(created)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {/* Content */}
                            <div className="mt-1">
                                {openEdit ? (
                                    <ResponsesEditAction onExit={toggleOpenEdit} item={item}/>
                                ) : (
                                    <div className="flex flex-col gap-1">
                                        {messageContent}
                                        {!!videoUrl && (
                                            <div className="mt-1" style={{maxWidth: isReply ? 260 : 360}}>
                                                <MediaPlayer url={videoUrl}/>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {/* Stats */}
                            {_.some([total_votes > 0, total_replies > 0]) && (
                                <div
                                    style={{ padding: '0 6px', bottom: -12, right: 10 }}
                                    className="flex flex-center background border border-color-100 rounded-full absolute">
                                    <div className="flex items-center gap-1">
                                        <Avatar.Group max={{count: 2}} className="items-center">
                                            <Avatar
                                                size={14} icon={<BiSolidLike size={8} className="text-white"/>}
                                                style={{ border: '1px solid #fff' }}
                                                className="flex flex-center bg-blue-600"
                                            />
                                            <Avatar
                                                size={14} icon={<BiSolidChat size={8} className="text-white"/>}
                                                style={{ border: '1px solid #fff' }}
                                                className="flex flex-center bg-green-500"
                                            />
                                        </Avatar.Group>
                                        <span className="font-medium text-xs text-color-100 tracking-wider">
                                        {formatNumber(Number(total_votes + total_replies))}
                                    </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={classNames('mt-2 flex flex-col', isReply && 'text-xs')}>
                            <div className="flex gap-4 mt-1">
                                {!isDeleted && hasExtraVote && (
                                    <div className="flex">
                                        <ExtraLike
                                            action={VoteTypes.UP}
                                            vote_status={vote_status}
                                            total_votes={total_votes ?? 0}
                                            onClick={() => vote(VoteTypes.UP)}
                                        />
                                        <ExtraLike
                                            action={VoteTypes.DOWN}
                                            vote_status={vote_status}
                                            onClick={() => vote(VoteTypes.DOWN)}
                                            total_downvotes={total_downvotes ?? 0}
                                        />
                                    </div>
                                )}
                                {showReplies && (
                                    <div
                                        onClick={toggleOpenReplies}
                                        className="flex items-center gap-1 text-gray-700 hover:text-secondary cursor-pointer">
                                        <GoDotFill size={8} />
                                        <span className="font-medium tracking-wide">{t('reply')}</span>
                                    </div>
                                )}
                                {renderIf(
                                    !isDeleted && canManage,
                                    <div className={classNames('flex items-center contextIcon', isReply ? 'gap-1' : 'gap-x-4')}>
                                        {isReply ? (
                                            <ActionIconButton
                                                onClick={toggleOpenEdit}
                                                colorClass="text-blue-500"
                                                icon={<FiEdit3 size={13}/>}
                                            />
                                        ) : (
                                            <span onClick={() => {toggleOpenEdit()}} className="flex items-center text-blue-500 hover:text-blue-700 cursor-pointer">
                                                <FiEdit3 size={13} className="mr-1"/>
                                                <span className="font-medium text-xs italic tracking-wide leading-tight">
                                                    {t('edit')}
                                                </span>
                                            </span>
                                        )}
                                        {isReply ? (
                                            <ActionIconButton
                                                onClick={confirmDeleteComment}
                                                colorClass="text-primary-600"
                                                icon={<FiTrash2 size={13}/>}
                                            />
                                        ) : (
                                            <span
                                                onClick={confirmDeleteComment}
                                                className="flex items-center text-primary-600 hover:text-primary cursor-pointer">
                                                <FiTrash2 size={12} className="mr-1"/>
                                                <span className="font-normal text-xs tracking-wide">{t('delete')}</span>
                                            </span>
                                        )}
                                    </div>
                                )}
                                {!isDeleted && !!!invisible && bestCommentAction}
                            </div>
                            {total_replies > 0 && (
                                <span
                                    onClick={toggleOpenReplies}
                                    className="mt-2 mx-2 font-medium text-slate-500 hover:text-secondary-600 cursor-pointer">
                                    <FaReply size={12} className="rotate-180 mr-1 text-slate-400"/>
                                    {t('view {n} community responses', {n: total_replies})}
                                </span>
                            )}
                            {openReplies && (
                                <div className="mt-2">
                                    <Replies
                                        id={container_id}
                                        type={container_type}
                                        comment_id={getId(item)}
                                        depth={depth + 1}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Wrapper>
    )
}

const renderComment =
    ({id, type, canReply = true, depth = 0}) =>
        (item) => {
            const isVoteComment = type === 'ask'
            const Component = isVoteComment ? VoteComment : Comment
            return (
                <Component
                    item={item}
                    key={item.id}
                    ref_id={id}
                    canReply={canReply}
                    depth={depth}
                />
            )
        }

export const Responses = ({type, id, Feature = NullAsDefault, renderEmpty = NullAsDefault}) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const dispatch = useDispatch()

    const {socket, authenticated} = useContext(SocketContext)

    const [refreshKey, setRefreshKey] = useState(Date.now())

    const {sortByDate: comments = []} = useSelectEntities(
        `${type}_${id}`,
        commentContainerSchema,
        {}
    )

    const login = useContext(LoginContext)

    useEffect(() => {
        if (socket && type && id) {
            socket.emit(JOIN_ROOM, {
                roomId: id,
                roomType: type,
            })
        }
    }, [id, type, socket])

    useEffect(() => {
        if (!socket) return
        let timeout
        if (authenticated) {
            socket.off('server-push-content')
            socket.on('server-push-content', (data) => {
                dispatch({
                    type: 'entities',
                    ...normalize(data, socketUnionSchema),
                })
            })
        }
        return () => {
            if (timeout) {
                clearTimeout(timeout)
            }
        }
    }, [authenticated, dispatch, socket])

    const comment_ids = Object.keys(comments)
        .sort((a, b) => Number(b) - Number(a))
        .map((key) => comments[key])

    return (
        <div className="mt-3 flex flex-col gap-4">
            {type === 'ask' && (
                <Feature/>
            )}
            <div className="flex items-center gap-3">
                <IoIosChatbubbles size={22} className="text-green-500"/>
                <div className="flex flex-col flex-1">
                    <h3 className="flex-1 font-bold text-sm md:text-base text-gray-900 leading-tight tracking-wider">
                        {type === 'ask' ? t('answers') : t('comments')}
                    </h3>
                    <div style={{ height: 3 }} className="my-1 w-20 bg-gray-400 rounded-full"/>
                    <p className="text-color-300 tracking-wider">
                        {t('comment guideline')}
                    </p>
                </div>
            </div>
            {!login ? (
                <div className="flex flex-col space-y-3 mb-16 mt-3">
                    <div className="flex flex-col flex-center gap-3 border border-dashed rounded-lg px-2 py-8">
                        <div className="text-center text-sm text-color-400 italic">
                            {t('Please log in to Kristo Network to leave your comment in the post')}
                        </div>
                        <Link
                            to={'/login'}
                            className="px-3 py-1 shadow-lg bg-primary-50 hover:bg-primary-100 rounded-lg text-primary text-sm no-text-shadow no-underline">
                            {t('Login now')}
                        </Link>
                    </div>
                    <GoogleAdsRecommendedUnitHorizontal/>
                </div>
            ) : (
                <div className="z-10">
                    {!!type && !!id && (
                        <AsyncAction
                            onSuccess={() => {
                                successNotify(NotificationActionTypes.CREATE, t)
                                setRefreshKey(Date.now())
                            }}
                            apiInfo={comment_addComment_Api}
                            query={{
                                ':prop': type,
                                ':id': id,
                            }}>
                            {({isLoading, handleAsyncAction}) => (
                                <div className="flex gap-3">
                                    <Avatar
                                        size={36}
                                        src={getAvatar(login)}
                                        icon={<BsPersonFill size={20} className="text-secondary-500"/>}
                                        className="flex flex-center bg-secondary-50"
                                    />
                                    <InputBar
                                        key={refreshKey}
                                        textOnly={true}
                                        rows={2}
                                        isLoading={!!isLoading}
                                        defaultHeight={68}
                                        placeholder={t('type comment')}
                                        handleSubmit={({description, photos = []}) => {
                                            handleAsyncAction({
                                                message: description,
                                                photos
                                            })
                                        }}
                                        className="flex-1 background"
                                    />
                                </div>
                            )}
                        </AsyncAction>
                    )}
                </div>
            )}
            {!!type && !!id && (
                <LazyPagination
                    query={{
                        ':prop': type,
                        ':id': id,
                    }}
                    renderLoading={renderLoading}
                    apiInfo={comment_getComments_Api}
                    refreshKey={refreshKey}
                    key={comment_getComments_Api.path.replace(':prop', type).replace(':id', id)}
                    renderEmpty={_.isEmpty(comment_ids) ? () => <EmptyHolder/> : NullAsDefault}
                    renderItem={(item) => (
                        <SelectEntityItem
                            item={item?.id}
                            defaultItem={item}
                            schema={commentSchema}>
                            {renderComment({id, type})}
                        </SelectEntityItem>
                    )}
                />
            )}
        </div>
    )
}

export const Replies = nest(({type, id, comment_id, depth = 1}) => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    const [refreshKey, setRefreshKey] = useState(Date.now())
    const {sortByDate: comments = []} = useSelectEntities(
        `${type}_${id}_${comment_id}`,
        commentContainerSchema,
        {}
    )

    return (
        <div className={classNames(
            'border-l border-color-100',
            depth <= MAX_INDENT_DEPTH ? 'pl-2' : 'pl-0'
        )}>
            {!login ? (
                <div className="flex flex-col flex-center gap-3 border border-dashed rounded-lg px-2 py-8">
                    <div className="text-center text-sm text-color-400 italic">
                        {t(
                            'Please log in to Kristo Network to leave your comment in the post'
                        )}
                    </div>
                    <Link
                        to={'/login'}
                        className="rounded-md px-3 py-1 bg-primary text-white text-sm no-text-shadow hover:text-white no-underline shadow-lg">
                        {t('Login now')}
                    </Link>
                </div>
            ) : (
                <div className="z-10">
                    <AsyncAction
                        onSuccess={() => {
                            successNotify(NotificationActionTypes.CREATE, t)
                            setRefreshKey(Date.now())
                        }}
                        apiInfo={comment_addReply_Api}
                        query={{
                            ':type': type,
                            ':id': id,
                            ':comment_id': comment_id,
                        }}>
                        {({isLoading, handleAsyncAction}) => {
                            return (
                                <InputBar
                                    key={refreshKey}
                                    textOnly={true}
                                    message={true}
                                    isLoading={!!isLoading}
                                    placeholder={t('reply')}
                                    className="bg-transparent"
                                    handleSubmit={logFunc(({description}) => {
                                        if (!description?.length) {
                                            return
                                        }
                                        handleAsyncAction({
                                            message: description,
                                        })
                                    })}
                                />
                            )
                        }}
                    </AsyncAction>
                </div>
            )}
            <div className="mt-2 flex-1 verticalList">
                {Object.keys(comments)
                    .sort((a, b) => Number(b) - Number(a))
                    .map((key) => comments[key])
                    .map((key) => (
                        <SelectEntityItem
                            key={key}
                            item={key}
                            schema={commentSchema}>
                            {renderComment({id, type, canReply: true, depth})}
                        </SelectEntityItem>
                    ))}
                <LazyPagination
                    renderEmpty={NullAsDefault}
                    query={{
                        ':type': type,
                        ':id': id,
                        ':comment_id': comment_id,
                    }}
                    apiInfo={comment_getReples_Api}
                    renderItem={(item) => (
                        <SelectEntityItem
                            item={item?.id}
                            defaultItem={item}
                            schema={commentSchema}>
                            {(item) => {
                                const ids = Object.values(comments)
                                const hideRecentComment = _.find(ids, (value) =>
                                    [_.get(item, 'id'), _.get(item, 'idname')].includes(value)
                                )
                                return hideRecentComment
                                    ? null
                                    : renderComment({id, type, canReply: true, depth})(item)
                            }}
                        </SelectEntityItem>
                    )}
                />
            </div>
        </div>
    )
})
